/**
 * Explore Landing Page - Unified Search for Universities, Programs & Countries
 * Main entry point for discovering educational opportunities
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, GraduationCap, BookOpen, Globe, TrendingUp, Award, DollarSign, BookText, Check, Briefcase, Compass, ArrowRight } from 'lucide-react';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardHeader, CardTitle, CardContent } from '@voilajsx/uikit/card';
import { SEO, Header, Footer } from '../../../shared/components';

const ExploreLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore/universities?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const quickFilters = [
    { label: '200 Universities', icon: GraduationCap, path: '/explore/universities', color: 'primary' },
    { label: '86 Programs', icon: BookOpen, path: '/explore/programs', color: 'success' },
    { label: '10 Countries', icon: Globe, path: '/explore/countries', color: 'info' },
    { label: '10 Career Paths', icon: Briefcase, path: '/explore/careers', color: 'warning' },
  ];

  const popularSearches = [
    'Computer Science',
    'Business',
    'Engineering',
    'United States',
    'Canada',
    'United Kingdom',
  ];

  const browseCategories = [
    { title: 'By Field', icon: BookOpen, items: ['Computer Science', 'Business', 'Engineering', 'Medicine'], path: '/explore/universities?field=' },
    { title: 'By Country', icon: Globe, items: ['United States', 'United Kingdom', 'Canada', 'Australia'], path: '/explore/universities?country=' },
    { title: 'By Budget', icon: DollarSign, items: ['Under $20k', '$20k-$40k', '$40k-$60k', 'Over $60k'], path: '/explore/universities?budget=' },
    { title: 'By Ranking', icon: Award, items: ['Top 50', 'Top 100', 'Top 200', 'All Rankings'], path: '/explore/universities?ranking=' },
  ];

  return (
    <>
      <SEO
        title="Explore Universities, Programs, Countries & Careers"
        description="Discover 200+ universities, 86 programs, 10 countries, and 33 career specializations. Find your perfect educational and career match with AskDino."
      />

      <Header />

      <div className="min-h-screen bg-background">
        {/* Hero Section with Search */}
        <div className="bg-gradient-to-br from-primary via-primary to-emerald-700 text-white py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto text-center">
              {/* Heading */}
              <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
                Understand Careers Deeply, Choose Universities Wisely
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed">
                AI-powered insights for informed career and educational decisions
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="mb-8">
                <div className="relative max-w-3xl mx-auto">
                  <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 h-6 w-6 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search for universities, programs, countries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-16 pr-6 py-5 rounded-2xl text-lg text-foreground bg-card border-2 border-transparent focus:border-primary focus:outline-none shadow-xl"
                  />
                  <Button
                    type="submit"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 rounded-xl font-semibold"
                  >
                    Search
                  </Button>
                </div>
              </form>

              {/* Popular Searches */}
              <div className="mb-8">
                <p className="text-sm text-white/70 mb-3">Popular searches:</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {popularSearches.map((term) => (
                    <button
                      key={term}
                      onClick={() => {
                        setSearchQuery(term);
                        navigate(`/explore/search?q=${encodeURIComponent(term)}`);
                      }}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full text-sm transition-all border border-white/20 cursor-pointer"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Filter Buttons */}
              <div className="flex flex-wrap justify-center gap-4">
                {quickFilters.map((filter) => {
                  const Icon = filter.icon;
                  return (
                    <button
                      key={filter.path}
                      onClick={() => navigate(filter.path)}
                      className="flex items-center gap-3 px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-xl border border-white/20 transition-all group cursor-pointer"
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-semibold">{filter.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Primary Features - Career Explorer, Quiz, Search */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 lg:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3">Start Your Journey</h2>
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground">Choose how you want to explore your future</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Career Explorer Card */}
            <div
              onClick={() => navigate('/explore/careers')}
              className="group bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 text-white cursor-pointer hover:shadow-2xl hover:scale-[1.02] lg:hover:scale-105 transition-all duration-300"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 sm:mb-6 lg:mb-8 group-hover:bg-white/30 transition-colors">
                <Briefcase className="h-7 w-7 sm:h-8 sm:w-8 lg:h-10 lg:w-10" />
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 lg:mb-4">Career Explorer</h3>
              <p className="text-white/90 mb-4 sm:mb-6 lg:mb-8 text-sm sm:text-base lg:text-lg leading-relaxed">
                Discover 10 career domains with 33 specializations. Deep-dive into career paths with salary insights, growth trends, and required skills.
              </p>
              <div className="flex items-center gap-2 text-sm sm:text-base lg:text-lg font-semibold group-hover:gap-3 transition-all">
                Explore Careers <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
            </div>

            {/* Career Quiz Card */}
            <div
              onClick={() => navigate('/explore/careers/match')}
              className="group bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 text-white cursor-pointer hover:shadow-2xl hover:scale-[1.02] lg:hover:scale-105 transition-all duration-300"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 sm:mb-6 lg:mb-8 group-hover:bg-white/30 transition-colors">
                <Compass className="h-7 w-7 sm:h-8 sm:w-8 lg:h-10 lg:w-10" />
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 lg:mb-4">Career Match Quiz</h3>
              <p className="text-white/90 mb-4 sm:mb-6 lg:mb-8 text-sm sm:text-base lg:text-lg leading-relaxed">
                Take our AI-powered 4-step quiz to find careers that match your interests, work style, and priorities. Get personalized recommendations.
              </p>
              <div className="flex items-center gap-2 text-sm sm:text-base lg:text-lg font-semibold group-hover:gap-3 transition-all">
                Take Quiz <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
            </div>

            {/* Search & Browse Card */}
            <div
              onClick={() => navigate('/explore/universities')}
              className="group bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 text-white cursor-pointer hover:shadow-2xl hover:scale-[1.02] lg:hover:scale-105 transition-all duration-300"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 sm:mb-6 lg:mb-8 group-hover:bg-white/30 transition-colors">
                <Search className="h-7 w-7 sm:h-8 sm:w-8 lg:h-10 lg:w-10" />
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 lg:mb-4">Search & Browse</h3>
              <p className="text-white/90 mb-4 sm:mb-6 lg:mb-8 text-sm sm:text-base lg:text-lg leading-relaxed">
                Search 200+ universities, 86 programs across 10 countries. Filter by field, budget, ranking, and location to find your perfect match.
              </p>
              <div className="flex items-center gap-2 text-sm sm:text-base lg:text-lg font-semibold group-hover:gap-3 transition-all">
                Start Searching <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 py-12 sm:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">200+</div>
                <div className="text-sm sm:text-base text-muted-foreground font-medium">Universities</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">86</div>
                <div className="text-sm sm:text-base text-muted-foreground font-medium">Programs</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">10</div>
                <div className="text-sm sm:text-base text-muted-foreground font-medium">Countries</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">33</div>
                <div className="text-sm sm:text-base text-muted-foreground font-medium">Career Paths</div>
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Test Prep - FirstAcademy */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 rounded-2xl sm:rounded-3xl p-6 sm:p-12">
              <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center gap-2 bg-orange-100 dark:bg-orange-900/30 px-4 py-2 rounded-full mb-4">
                <TrendingUp className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">TEST PREPARATION</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">Ace Your Admissions Tests</h2>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">Expert coaching from FirstAcademy to boost your scores</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* IELTS Card */}
              <Card className="border-primary/20 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.open('https://firstacademy.in/ielts', '_blank')}>
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <BookText className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">IELTS Coaching</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">Target Band 8 with expert trainers</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" />
                      <span>Live Classes</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" />
                      <span>Mock Tests</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SAT Card */}
              <Card className="border-primary/20 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.open('https://firstacademy.in/sat', '_blank')}>
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <GraduationCap className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">SAT Prep</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">Average score 1500+</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" />
                      <span>Expert Faculty</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" />
                      <span>Practice Tests</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* GRE Card */}
              <Card className="border-primary/20 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.open('https://firstacademy.in/gre', '_blank')}>
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Award className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">GRE Training</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">Target 320+ score</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" />
                      <span>Verbal + Quant</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" />
                      <span>AWA Guidance</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* TOEFL Card */}
              <Card className="border-primary/20 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.open('https://firstacademy.in/toefl', '_blank')}>
                <CardHeader>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Globe className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">TOEFL Prep</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">Score 100+ confidently</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" />
                      <span>All 4 Sections</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary" />
                      <span>Speaking Practice</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

              <div className="text-center mt-6 sm:mt-8">
                <Button
                  onClick={() => window.open('https://firstacademy.in/', '_blank')}
                  className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-xl shadow-lg"
                >
                  Explore All Test Prep Programs →
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA Section - Split Design */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
            {/* Career Path CTA */}
            <div
              onClick={() => navigate('/explore/careers/match')}
              className="group relative bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 rounded-2xl sm:rounded-3xl p-8 sm:p-10 lg:p-12 text-white cursor-pointer hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6 lg:mb-8 group-hover:scale-110 transition-transform">
                  <Briefcase className="h-8 w-8 lg:h-10 lg:w-10" />
                </div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Discover Your Career</h3>
                <p className="text-white/90 text-base sm:text-lg lg:text-xl mb-6 lg:mb-8 leading-relaxed">
                  Take our AI-powered quiz to match with careers that fit your interests and goals
                </p>
                <div className="flex items-center gap-2 text-lg font-semibold group-hover:gap-3 transition-all">
                  Start Career Quiz <ArrowRight className="h-5 w-5" />
                </div>

                {/* Stats badge */}
                <div className="mt-8 pt-8 border-t border-white/20">
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <div className="text-2xl font-bold">10</div>
                      <div className="text-white/70">Domains</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">33</div>
                      <div className="text-white/70">Careers</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">4</div>
                      <div className="text-white/70">Minutes</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* University Match CTA */}
            <div
              onClick={() => navigate('/quiz')}
              className="group relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl sm:rounded-3xl p-8 sm:p-10 lg:p-12 text-white cursor-pointer hover:shadow-2xl transition-all duration-300 overflow-hidden"
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6 lg:mb-8 group-hover:scale-110 transition-transform">
                  <GraduationCap className="h-8 w-8 lg:h-10 lg:w-10" />
                </div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Find Your University</h3>
                <p className="text-white/90 text-base sm:text-lg lg:text-xl mb-6 lg:mb-8 leading-relaxed">
                  Get matched with universities and programs based on your preferences and qualifications
                </p>
                <div className="flex items-center gap-2 text-lg font-semibold group-hover:gap-3 transition-all">
                  Start University Quiz <ArrowRight className="h-5 w-5" />
                </div>

                {/* Stats badge */}
                <div className="mt-8 pt-8 border-t border-white/20">
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <div className="text-2xl font-bold">200+</div>
                      <div className="text-white/70">Universities</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">86</div>
                      <div className="text-white/70">Programs</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">10</div>
                      <div className="text-white/70">Countries</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ExploreLandingPage;
